import { useEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * Global scroll manager.
 *
 * - Back/forward (POP): restore the exact scroll offset of that history entry,
 *   i.e. return the user to the section they were browsing before opening a
 *   detail page.
 * - Forward navigation (PUSH): start at the top; the only exception is an
 *   explicit `scrollTo` section target (used by the navbar section links) which
 *   the target page handles itself.
 * - The browser's native scroll restoration is disabled so it cannot fight with
 *   the logic above.
 *
 * Notes:
 * - The very first history entry of a page load has the key "default". Users
 *   usually enter the site on the home page, so "default" must NOT be treated
 *   as "fresh load" when they come back to it later — otherwise the position
 *   is never restored. A real fresh load cannot have a saved offset, because
 *   the map below only lives in memory.
 * - The offset is recorded synchronously inside the scroll handler. Doing it in
 *   a deferred rAF callback risks writing 0 after the router already reset the
 *   scroll position of the next page, which used to lose the position.
 */
const scrollPositions = new Map<string, number>();

/** History entry whose scroll offset is being restored programmatically. */
let restoringKey: string | null = null;

/**
 * History entry that is navigating away right now. While a navigation commits,
 * the outgoing page is unmounted and the (possibly much shorter) incoming page
 * clamps `window.scrollY`; those clamp-induced scroll events must not overwrite
 * the offset that was just captured for the outgoing entry.
 */
let leavingKey: string | null = null;

/** Restoring keeps retrying while async content grows the page, then gives up. */
const MAX_RESTORE_MS = 2500;

function scrollToInstant(y: number) {
  // `html { scroll-behavior: smooth }` makes a plain `window.scrollTo(0, y)`
  // animate. In a background tab the animation never advances, so the jump
  // silently fails. Passing an explicit "instant" behaviour bypasses the CSS
  // value and applies the offset synchronously.
  window.scrollTo({ top: Math.max(0, y), behavior: 'instant' });
}

export default function ScrollManager() {
  const location = useLocation();
  const navigationType = useNavigationType();

  // Disable the browser's native scroll restoration: on back/forward it would
  // asynchronously restore the previous offset on its own and fight with the
  // logic below. We fully own scroll behaviour here.
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // Continuously remember the scroll offset of the current history entry, so a
  // later back/forward can restore it (the exact section the user left from).
  useEffect(() => {
    const key = location.key;
    leavingKey = null;

    const save = () => {
      // Ignore the scroll events produced by our own restore and by the clamp
      // that happens while this entry is being left.
      if (restoringKey === key || leavingKey === key) return;
      scrollPositions.set(key, window.scrollY);
    };

    // Capture the offset *before* the router navigates. Effect cleanup runs
    // after the next page is committed, when `window.scrollY` has already been
    // clamped to the shorter page — saving there would lose the position.
    const captureBeforeLeaving = () => {
      if (restoringKey === key) return;
      scrollPositions.set(key, window.scrollY);
      leavingKey = key;
      // A click may not navigate (modifier keys, prevented default); resume
      // normal recording shortly after if this entry is still active.
      window.setTimeout(() => {
        if (leavingKey === key) leavingKey = null;
      }, 500);
    };

    window.addEventListener('scroll', save, { passive: true });
    document.addEventListener('click', captureBeforeLeaving, true);
    window.addEventListener('popstate', captureBeforeLeaving);
    return () => {
      window.removeEventListener('scroll', save);
      document.removeEventListener('click', captureBeforeLeaving, true);
      window.removeEventListener('popstate', captureBeforeLeaving);
    };
  }, [location.key]);

  useEffect(() => {
    // A `scrollTo` section target is only meaningful for forward navigation;
    // the target page scrolls to that section itself.
    const target =
      navigationType === 'PUSH'
        ? (location.state as { scrollTo?: string } | null)?.scrollTo
        : undefined;
    if (target) return undefined;

    if (navigationType !== 'POP') {
      scrollToInstant(0);
      // The incoming page may not have been laid out yet when the effect runs
      // (e.g. a background tab); confirm the reset shortly after.
      const timer = window.setTimeout(() => scrollToInstant(0), 150);
      return () => window.clearTimeout(timer);
    }

    // Back/forward: restore the offset saved for this history entry so the user
    // lands back on the section they were browsing before leaving.
    const saved = scrollPositions.get(location.key);
    if (saved === undefined || saved <= 0) {
      scrollToInstant(0);
      return undefined;
    }

    let stopped = false;
    let timer = 0;
    const startedAt = performance.now();
    restoringKey = location.key;

    const stop = () => {
      if (stopped) return;
      stopped = true;
      window.clearTimeout(timer);
      window.removeEventListener('wheel', stop);
      window.removeEventListener('touchstart', stop);
      window.removeEventListener('keydown', stop);
      if (restoringKey === location.key) restoringKey = null;
    };

    const tick = () => {
      if (stopped) return;
      // Content may render asynchronously after a back navigation (images,
      // fetched sections). Keep re-applying the offset while the page grows
      // until the saved position is reachable, then stop.
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      const y = Math.min(saved, Math.max(maxScroll, 0));
      if (Math.abs(window.scrollY - y) > 1) scrollToInstant(y);
      if (Math.abs(window.scrollY - saved) <= 2 || performance.now() - startedAt >= MAX_RESTORE_MS) {
        stop();
        return;
      }
      // A timer instead of rAF: rAF is paused in background tabs.
      timer = window.setTimeout(tick, 60);
    };

    // As soon as the user scrolls/touches/keys, hand control back to them and
    // let their position be recorded normally.
    window.addEventListener('wheel', stop, { passive: true });
    window.addEventListener('touchstart', stop, { passive: true });
    window.addEventListener('keydown', stop);
    tick();

    return stop;
  }, [location.key, location.pathname, location.state, navigationType]);

  return null;
}
